// ‑‑ Constants ---------------------------------------------------
const EXCLUDED = new Set(['hidden', 'exclude']);

export interface Migration {
	path: string;
	defaultValue: unknown;
}
export interface MigrationDiff {
	additions: Migration[];
	removals: string[];
}

// ‑‑ Public API --------------------------------------------------
export function diffConfigs(
	defaults: any,
	uploaded: any,
	prefix = ''
): MigrationDiff {
	const additions: Migration[] = [];
	const removals: string[] = [];

	// check keys in defaults
	for (const [k, dv] of Object.entries(defaults)) {
		if (EXCLUDED.has(k)) continue;
		const path = prefix ? `${prefix}.${k}` : k;

		if (!(k in uploaded)) {
			additions.push({ path, defaultValue: dv });
			continue;
		}
		const uv = uploaded[k];

		// nested object
		if (isObj(dv) && isObj(uv)) {
			const sub = diffConfigs(dv, uv, path);
			additions.push(...sub.additions);
			removals.push(...sub.removals);
		}
	}

	// extras in upload
	for (const [k, uv] of Object.entries(uploaded)) {
		if (EXCLUDED.has(k)) continue;
		const path = prefix ? `${prefix}.${k}` : k;
		if (!(k in defaults)) {
			removals.push(path);
			continue;
		}
		const dv = defaults[k];
		if (isObj(uv) && isObj(dv)) {
			const sub = diffConfigs(dv, uv, path);
			additions.push(...sub.additions);
			removals.push(...sub.removals);
		}
	}
	return { additions, removals };
}

export function mergeAndPrune(defaults: any, uploaded: any): any {
	const out: any = {};

	// 1) defaults keys first (ordered)
	for (const [k, dv] of Object.entries(defaults)) {
		if (EXCLUDED.has(k)) {
			// -------- preserve uploaded value verbatim, if present --------
			if (k in uploaded) out[k] = uploaded[k];
			continue; // but do NOT recurse / add defaults
		}

		if (k in uploaded) {
			const uv = uploaded[k];
			if (isObj(dv) && isObj(uv)) out[k] = mergeAndPrune(dv, uv);
			else out[k] = uv; // keep user value
		} else {
			out[k] = dv; // add missing default
		}
	}

	// 2) brand-new keys from upload (except excluded)
	for (const [k, uv] of Object.entries(uploaded)) {
		if (EXCLUDED.has(k) || k in defaults) continue;
		out[k] = uv;
	}

	return out;
}

// ‑‑ Helpers -----------------------------------------------------
export function isObj(v: unknown): v is Record<string, any> {
	return !!v && typeof v === 'object' && !Array.isArray(v);
}

export function diffArrayWithProto(
	proto: any,
	arr: any[],
	prefix: string
): MigrationDiff {
	const result: MigrationDiff = { additions: [], removals: [] };
	arr.forEach((el, i) => {
		if (isObj(el)) {
			const d = diffConfigs(proto, el, `${prefix}[${i}]`);
			result.additions.push(...d.additions);
			result.removals.push(...d.removals);
		}
	});
	return result;
}

export function mergeArrayWithProto(proto: any, arr: any[]): any[] {
	return arr.map(el => (isObj(el) ? mergeAndPrune(proto, el) : el));
}

// remove top‑level "complete" and force every nested .complete = false
export function stripAndNormalizeComplete(obj: any): void {
	if (!isObj(obj)) return;
	if (obj.complete !== undefined) obj.complete = false;
	for (const v of Object.values(obj)) {
		if (isObj(v)) stripAndNormalizeComplete(v);
		else if (Array.isArray(v)) v.forEach(stripAndNormalizeComplete);
	}
}

export function combineDiffs(
	a: MigrationDiff,
	b: MigrationDiff
): MigrationDiff {
	return {
		additions: [...a.additions, ...b.additions],
		removals: [...a.removals, ...b.removals],
	};
}

export function mergeFieldArray(
	obj: any,
	field: string,
	protoArr: any[],
	pathPrefix: string,
	diffAcc: MigrationDiff
) {
	if (Array.isArray(obj[field]) && protoArr.length) {
		const proto = protoArr[0];
		const localArr = obj[field];

		const d = diffArrayWithProto(proto, localArr, pathPrefix);
		diffAcc.additions.push(...d.additions);
		diffAcc.removals.push(...d.removals);

		obj[field] = mergeArrayWithProto(proto, localArr);
	}
}

export function dedupDiff(d: MigrationDiff): MigrationDiff {
	const seenAdd = new Set<string>();
	const uniqAdd = d.additions.filter(m => {
		if (seenAdd.has(m.path)) return false;
		seenAdd.add(m.path);
		return true;
	});

	const uniqRem = Array.from(new Set(d.removals));

	return { additions: uniqAdd, removals: uniqRem };
}
